import { FormEvent, useEffect, useState } from 'react';
import {
  ChevronLeft,
  Dices,
  Gamepad2,
  Heart,
  Home,
  LockKeyhole,
  LogOut,
  Monitor,
  Search,
  ShieldCheck,
} from 'lucide-react';
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import type {
  CollectionSummary,
  GameCopy,
  GameRecord,
  PlatformSummary,
} from '@pixel-archive/shared';
import type { SearchSuggestion, ViewerHardware } from './supabase';
import {
  configured,
  getCollectionSummary,
  getGame,
  getGameCopies,
  getPlatform,
  getPlatformGames,
  getPlatformHardware,
  getPlatforms,
  getSearchSuggestions,
  getWishlist,
  searchCollection,
  searchCollectionEntities,
  supabase,
} from './supabase';
import { invertedPlatformLogos, platformLogoFile } from './platformLogos';
import { RediscoverPage } from './RediscoverPage';

export function App() {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!supabase) {
      setReady(true);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      setSignedIn(Boolean(data.session));
      setReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      setSignedIn(Boolean(session));
      if (event === 'PASSWORD_RECOVERY' || window.location.hash.includes('type=recovery')) {
        navigate('/reset-password', { replace: true });
      }
    });
    return () => data.subscription.unsubscribe();
  }, [navigate]);
  if (!ready) return <Splash />;
  return (
    <Routes>
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/login"
        element={signedIn ? <Navigate to={loginTarget(location.state)} replace /> : <Login />}
      />
      <Route
        path="*"
        element={
          signedIn ? (
            <Viewer />
          ) : (
            <Navigate to="/login" replace state={{ from: pageUrl(location) }} />
          )
        }
      />
    </Routes>
  );
}

function Viewer() {
  return (
    <div className="viewer">
      <ViewerHeader />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/games" element={<SearchPage />} />
          <Route path="/games/:id" element={<GamePage />} />
          <Route path="/platforms" element={<PlatformsPage />} />
          <Route path="/platforms/:id" element={<PlatformPage />} />
          <Route path="/machines" element={<PlatformsPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route
            path="/redecouvrir"
            element={<RediscoverPage renderCover={(game) => <Cover game={game} />} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <ViewerBottomNav />
    </div>
  );
}
function ViewerHeader() {
  return (
    <header className="viewer-header">
      <Link to="/" className="viewer-brand">
        <img src="/logo.png" alt="Turk182! Pixel Archive" />
        <span>
          <strong>Turk182!</strong>
          <small>Pixel Archive</small>
        </span>
      </Link>
      <div className="readonly">
        <LockKeyhole />
        <span>
          <strong>100% consultation</strong>
          <small>Aucune modification possible</small>
        </span>
      </div>
      <button aria-label="Se déconnecter" onClick={() => supabase?.auth.signOut()}>
        <LogOut />
      </button>
    </header>
  );
}

function HomePage() {
  const [loadState, setLoadState] = useState<'loading' | 'success' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [summary, setSummary] = useState<CollectionSummary | null>(null);
  const [platforms, setPlatforms] = useState<PlatformSummary[]>([]);
  useEffect(() => {
    let active = true;
    setLoadState('loading');
    Promise.all([getCollectionSummary(), getPlatforms()])
      .then(([s, p]) => {
        if (!active) return;
        setSummary(s);
        setPlatforms(p);
        setLoadState('success');
      })
      .catch(() => {
        if (active) setLoadState('error');
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  return (
    <div className="mobile-page">
      <span className="mode">Mode magasin · Lecture seule</span>
      <h1>Est-ce que je l’ai&nbsp;?</h1>
      <p className="lead">Vérifiez votre collection en quelques secondes.</p>
      <SearchBox autoFocus />
      <Link className="rediscover-entry" to="/redecouvrir">
        <Dices aria-hidden="true" />
        <span>
          <strong>Redécouvrir ma collection</strong>
          <small>Quel jeu allez-vous redécouvrir aujourd’hui ?</small>
        </span>
        <span aria-hidden="true">›</span>
      </Link>
      {loadState === 'loading' && <p role="status">Chargement de votre collection…</p>}
      {loadState === 'error' && (
        <div className="error-state" role="alert">
          <p>Impossible de charger les données. Vérifiez votre connexion et réessayez.</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>
            Réessayer
          </button>
        </div>
      )}
      {loadState === 'success' && summary && (
        <section className="summary">
          <article>
            <ShieldCheck />
            <strong>{summary.games.toLocaleString('fr-FR')}</strong>
            <span>Dans ma collection</span>
          </article>
          <article>
            <Heart />
            <strong>{summary.wishlist.toLocaleString('fr-FR')}</strong>
            <span>Wishlist</span>
          </article>
          <article>
            <Monitor />
            <strong>{summary.platforms}</strong>
            <span>Plateformes</span>
          </article>
        </section>
      )}
      {loadState === 'success' && (
        <section>
          <div className="section-title">
            <h2>Plateformes principales</h2>
            <Link to="/platforms">Voir tout</Link>
          </div>
          <div className="platform-row">
            {platforms.slice(0, 6).map((p) => (
              <Link to={`/platforms/${encodeURIComponent(p.id)}`} key={p.id}>
                <PlatformLogo platform={p} />
                <strong>{p.name}</strong>
                <span>{p.ownedCount} jeux</span>
              </Link>
            ))}
          </div>
          {!platforms.length && <p>Aucune plateforme dans votre collection pour le moment.</p>}
        </section>
      )}
    </div>
  );
}
function SearchBox({ autoFocus = false }: { autoFocus?: boolean }) {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    let active = true;
    setSuggestions([]);
    const id = setTimeout(() => {
      if (value.trim().length < 2) {
        setSuggestions([]);
        return;
      }
      getSearchSuggestions(value)
        .then((rows) => {
          if (active) setSuggestions(rows);
        })
        .catch(() => {
          if (active) setSuggestions([]);
        });
    }, 180);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [value]);
  const suggestionUrl = (item: SearchSuggestion) => {
    if (item.kind === 'game') return `/games/${encodeURIComponent(item.id)}`;
    return item.platformId
      ? `/platforms/${encodeURIComponent(item.platformId)}#hardware`
      : `/search?q=${encodeURIComponent(item.label)}`;
  };
  return (
    <form
      className="mobile-search search-with-suggestions"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) nav(`/search?q=${encodeURIComponent(value.trim())}`);
      }}
    >
      <Search />
      <input
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Rechercher un jeu, une machine ou du hardware…"
        aria-label="Recherche dans la collection"
      />
      <button>Rechercher</button>
      {open && value.trim().length >= 2 && (
        <div className="search-suggestions">
          {suggestions.map((item) => (
            <Link
              key={`${item.kind}:${item.id}`}
              to={suggestionUrl(item)}
              state={{ returnTo: `/search?q=${encodeURIComponent(value.trim())}` }}
              onClick={() => setOpen(false)}
            >
              {item.kind === 'game' ? <Gamepad2 /> : <Monitor />}
              <span>
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
              </span>
            </Link>
          ))}
          {!suggestions.length && <p>Aucune proposition</p>}
        </div>
      )}
    </form>
  );
}
function SearchPage() {
  const location = useLocation();
  const nav = useNavigate();
  const query = new URLSearchParams(location.search).get('q') ?? '';
  const [results, setResults] = useState<GameRecord[]>([]);
  const [entityResults, setEntityResults] = useState<SearchSuggestion[]>([]);
  const [loading, setLoading] = useState(query.trim().length >= 2);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setResults([]);
    setEntityResults([]);
    setError('');
    setLoading(query.trim().length >= 2);
    const id = setTimeout(() => {
      if (query.trim().length < 2) {
        setResults([]);
        setEntityResults([]);
        return;
      }
      setLoading(true);
      Promise.all([searchCollection(query.trim()), searchCollectionEntities(query.trim())])
        .then(([games, suggestions]) => {
          if (!active) return;
          setResults(games);
          setEntityResults(suggestions);
          setError('');
        })
        .catch((e) => {
          if (active) setError(String(e));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 220);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [query]);
  return (
    <div className="mobile-page">
      <span className="mode">Recherche ultra rapide</span>
      <h1>Ma collection</h1>
      <div className="mobile-search live">
        <Search />
        <input
          autoFocus
          value={query}
          onChange={(e) => {
            nav(`/search?q=${encodeURIComponent(e.target.value)}`, { replace: true });
          }}
          placeholder="Titre, plateforme, éditeur…"
        />
        <span>{loading ? '…' : results.length + entityResults.length}</span>
      </div>
      {error && <ErrorState text={error} />}
      <div className="results">
        {entityResults.map((item) => (
          <SearchEntityResult item={item} key={`${item.kind}:${item.id}`} />
        ))}
        {results.map((g) => (
          <GameResultCard game={g} key={g.id} />
        ))}
        {!loading && query.length > 1 && !results.length && !entityResults.length && !error && (
          <EmptyState text="Aucun résultat dans les données autorisées." />
        )}
      </div>
    </div>
  );
}

function SearchEntityResult({ item }: { item: SearchSuggestion }) {
  const location = useLocation();
  if (item.kind === 'game') return null;
  const target = item.platformId
    ? `/platforms/${encodeURIComponent(item.platformId)}#hardware`
    : '/platforms';
  return (
    <Link
      className="game-result search-entity-result"
      to={target}
      state={{ returnTo: pageUrl(location) }}
    >
      <div className="search-entity-icon">
        <Monitor />
      </div>
      <div>
        <strong>{item.label}</strong>
        <span>{item.detail}</span>
        <em>Hardware possédé</em>
      </div>
      <b>›</b>
    </Link>
  );
}
const pageUrl = (location: { pathname: string; search: string; hash: string }) =>
  location.pathname + location.search + location.hash;

// Only restore collection pages; a direct link has no navigation state.
function returnTarget(state: unknown, key = 'returnTo'): string | undefined {
  if (!state || typeof state !== 'object') return;
  const target = (state as Record<string, unknown>)[key];
  if (
    typeof target === 'string' &&
    /^\/(?:search|games|platforms(?:\/[^/?#]+)?|machines|wishlist)(?:[?#].*)?$/.test(target)
  )
    return target;
}

function GameResultCard({ game }: { game: GameRecord }) {
  const location = useLocation();
  const parent = returnTarget(location.state);
  return (
    <Link
      className="game-result"
      to={`/games/${encodeURIComponent(game.id)}`}
      state={{ returnTo: pageUrl(location), parentReturnTo: parent }}
    >
      <Cover game={game} />
      <div>
        <strong>{game.title}</strong>
        <span>
          {game.platformName}
          {game.publisher ? ` · ${game.publisher}` : ''}
        </span>
        <Status game={game} />
      </div>
      <b>›</b>
    </Link>
  );
}
function Status({ game }: { game: GameRecord }) {
  return (
    <span className={`status ${game.status}`}>
      {game.status === 'owned'
        ? `✓ DANS MA COLLECTION${game.copyCount > 1 ? ` · ${game.copyCount} EXEMPLAIRES` : ''}`
        : game.status === 'wishlist'
          ? '♡ WISHLIST'
          : '⊗ PAS DANS MA COLLECTION'}
    </span>
  );
}
function GamePage() {
  const { id } = useParams();
  const location = useLocation();
  const [game, setGame] = useState<GameRecord | null>(null);
  const [copies, setCopies] = useState<GameCopy[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (id)
      Promise.all([getGame(id), getGameCopies(id)])
        .then(([g, c]) => {
          setGame(g);
          setCopies(c);
        })
        .catch((e) => setError(String(e)));
  }, [id]);
  if (error) return <ErrorState text={error} />;
  if (!game) return <Splash />;
  return (
    <div className="mobile-page game-page">
      <Link
        className="back"
        to={returnTarget(location.state) ?? '/search'}
        state={{ returnTo: returnTarget(location.state, 'parentReturnTo') }}
      >
        <ChevronLeft />
        Retour aux résultats
      </Link>
      <div className="game-hero">
        <Cover game={game} />
        <div>
          <span>{game.platformName}</span>
          <h1>{game.title}</h1>
          <Status game={game} />
          {game.publisher && (
            <p>
              {game.publisher}
              {game.releaseYear ? ` · ${game.releaseYear}` : ''}
            </p>
          )}
        </div>
      </div>
      <h2>Mes exemplaires</h2>
      {copies.length ? (
        copies.map((c, i) => (
          <details open={i === 0} key={c.id}>
            <summary>Exemplaire {i + 1}</summary>
            <dl>
              <Info label="Région" value={c.region} />
              <Info label="Édition" value={c.edition} />
              <Info label="État" value={c.condition} />
              <Info label="Boîte" value={bool(c.hasBox)} />
              <Info label="Manuel" value={bool(c.hasManual)} />
              <Info label="Support physique" value={mediaTypeLabel(c)} />
              <Info label="Notes" value={c.notes} />
            </dl>
          </details>
        ))
      ) : (
        <EmptyState text="Aucun détail supplémentaire n’est renseigné." />
      )}
    </div>
  );
}
const bool = (v: boolean | null) => (v === null ? null : v ? 'Oui' : 'Non');
const mediaTypeLabel = (copy: GameCopy): string | null => {
  if (copy.hasMedia === false) return 'Absent';
  const labels = {
    cartouche: 'Cartouche',
    integre: 'Jeu intégré',
    cassette: 'Cassette',
    disquette: 'Disquette',
    optique: 'Disque optique',
  } as const;
  return copy.mediaType ? labels[copy.mediaType] : copy.hasMedia ? 'Présent' : null;
};
function Info({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
function PlatformsPage() {
  const location = useLocation();
  const [items, setItems] = useState<PlatformSummary[]>([]);
  useEffect(() => {
    getPlatforms()
      .then(setItems)
      .catch(() => {});
  }, []);
  return (
    <div className="mobile-page">
      <span className="mode">Consultation</span>
      <h1>Machines & plateformes</h1>
      <div className="platform-list-view">
        {items.map((p) => (
          <Link
            to={`/platforms/${encodeURIComponent(p.id)}`}
            state={{ returnTo: pageUrl(location) }}
            key={p.id}
          >
            <PlatformLogo platform={p} />
            <div>
              <strong>{p.name}</strong>
              <span>{p.ownedCount.toLocaleString('fr-FR')} jeux possédés</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function PlatformPage() {
  const { id } = useParams();
  const location = useLocation();
  const back = returnTarget(location.state);
  const [platform, setPlatform] = useState<PlatformSummary | null>(null);
  const [games, setGames] = useState<GameRecord[]>([]);
  const [hardware, setHardware] = useState<ViewerHardware[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!id) return;
    Promise.all([getPlatform(id), getPlatformGames(id), getPlatformHardware(id)])
      .then(([platformResult, gameResults, hardwareResults]) => {
        setPlatform(platformResult);
        setGames(gameResults);
        setHardware(hardwareResults);
      })
      .catch((reason) => setError(String(reason)));
  }, [id]);
  if (error) return <ErrorState text={error} />;
  if (!platform) return <Splash />;
  return (
    <div className="mobile-page platform-page">
      <Link className="back" to={back ?? '/platforms'}>
        <ChevronLeft />
        {back && /^\/(search|games)([?#]|$)/.test(back)
          ? 'Retour aux résultats'
          : 'Toutes les machines'}
      </Link>
      <span className="mode">Machine · Collection possédée</span>
      <div className="platform-page-heading">
        <PlatformLogo platform={platform} />
        <h1>{platform.name}</h1>
      </div>
      <nav className="platform-jump-links">
        <Link to="#games" state={location.state}>
          Jeux possédés ({games.length})
        </Link>
        <Link to="#hardware" state={location.state}>
          Hardware possédé ({hardware.length})
        </Link>
      </nav>
      <section id="games">
        <h2>Jeux possédés</h2>
        <div className="results">
          {games.map((game) => (
            <GameResultCard game={game} key={game.id} />
          ))}
          {!games.length && <EmptyState text="Aucun jeu possédé pour cette machine." />}
        </div>
      </section>
      <section id="hardware">
        <h2>Hardware possédé</h2>
        <div className="hardware-list-view">
          {hardware.map((item) => (
            <HardwareCard item={item} key={item.id} />
          ))}
          {!hardware.length && <EmptyState text="Aucun matériel associé à cette machine." />}
        </div>
      </section>
    </div>
  );
}

function HardwareCard({ item }: { item: ViewerHardware }) {
  const remoteCover = item.coverUrl && /^https?:\/\//i.test(item.coverUrl) ? item.coverUrl : null;
  return (
    <article className="hardware-card">
      {remoteCover ? (
        <img src={remoteCover} alt={item.name} />
      ) : (
        <div className="hardware-placeholder">
          <Monitor />
        </div>
      )}
      <div>
        <strong>{item.name}</strong>
        <span>{item.category || 'Matériel'}</span>
        <b>
          {item.quantity} exemplaire{item.quantity > 1 ? 's' : ''}
        </b>
        {item.notes && <small>{item.notes}</small>}
      </div>
    </article>
  );
}
function WishlistPage() {
  const [items, setItems] = useState<GameRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    getWishlist()
      .then((games) => {
        if (active) {
          setItems(games);
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError('Impossible de récupérer la Wishlist. Réessayez dans quelques instants.');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);
  return (
    <div className="mobile-page">
      <span className="mode">Consultation</span>
      <h1>Wishlist</h1>
      {loading && <p role="status">Chargement de la Wishlist…</p>}
      {error && <ErrorState text={error} />}
      <div className="results">
        {items.map((g) => (
          <GameResultCard game={g} key={g.id} />
        ))}
        {!loading && !error && !items.length && (
          <EmptyState text="Aucun élément de wishlist dans les données autorisées." />
        )}
      </div>
    </div>
  );
}
function loginTarget(state: unknown): string {
  const from = state && typeof state === 'object' ? (state as { from?: unknown }).from : null;
  return typeof from === 'string' &&
    /^\/(?:search|games(?:\/[^/?#]+)?|platforms(?:\/[^/?#]+)?|machines|wishlist|redecouvrir)(?:[?#].*)?$/.test(
      from,
    )
    ? from
    : '/';
}

function Login() {
  const location = useLocation();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError('Le site doit d’abord être publié et relié à votre espace Supabase.');
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) setError('Adresse ou mot de passe incorrect.');
    else nav(loginTarget(location.state), { replace: true });
  };
  const requestPassword = async () => {
    if (!supabase) return;
    if (!email.trim()) {
      setError("Saisissez d'abord votre adresse email.");
      return;
    }
    setBusy(true);
    setError('');
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) setError("L'e-mail n'a pas pu être envoyé. Réessayez dans quelques instants.");
    else setRecoverySent(true);
  };
  return (
    <main className="login">
      <img src="/logo.png" alt="Turk182! Pixel Archive" />
      <section>
        <LockKeyhole />
        <span className="mode">Web consultation · Mode magasin</span>
        <h1>Accéder à ma collection</h1>
        <p>Connexion privée. Ce site ne permet aucune modification.</p>
        <form onSubmit={submit}>
          <label>
            Adresse email
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Mot de passe
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button className="text-button" type="button" disabled={busy} onClick={requestPassword}>
            Mot de passe oublié ou jamais créé ?
          </button>
          {!configured && (
            <p className="notice">Le déploiement en ligne n’est pas encore configuré.</p>
          )}
          {error && <p className="error">{error}</p>}
          {recoverySent && (
            <p className="success">
              E-mail envoyé. Ouvrez-le puis touchez le lien pour choisir votre mot de passe.
            </p>
          )}
          <button disabled={busy}>{busy ? 'Connexion…' : 'Se connecter'}</button>
        </form>
      </section>
    </main>
  );
}

function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabase) return;
    if (password.length < 8) {
      setError('Choisissez un mot de passe contenant au moins 8 caractères.');
      return;
    }
    if (password !== confirmation) {
      setError('Les deux mots de passe ne sont pas identiques.');
      return;
    }
    setBusy(true);
    setError('');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setBusy(false);
      setError(
        'Le lien a expiré ou le mot de passe ne peut pas être enregistré. Demandez un nouveau lien.',
      );
      return;
    }
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };
  return (
    <main className="login">
      <img src="/logo.png" alt="Turk182! Pixel Archive" />
      <section>
        <LockKeyhole />
        <span className="mode">Connexion sécurisée</span>
        <h1>Choisir mon mot de passe</h1>
        <p>Créez le mot de passe qui servira à consulter votre collection.</p>
        <form onSubmit={submit}>
          <label>
            Nouveau mot de passe
            <input
              type="password"
              minLength={8}
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label>
            Confirmer le mot de passe
            <input
              type="password"
              minLength={8}
              required
              autoComplete="new-password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button disabled={busy}>
            {busy ? 'Enregistrement…' : 'Enregistrer mon mot de passe'}
          </button>
        </form>
      </section>
    </main>
  );
}
function ViewerBottomNav() {
  return (
    <nav className="bottom-nav" aria-label="Navigation mobile">
      <NavLink to="/">
        <Home />
        <span>Accueil</span>
      </NavLink>
      <NavLink to="/games">
        <Gamepad2 />
        <span>Jeux</span>
      </NavLink>
      <NavLink className="search-main" to="/search">
        <Search />
        <span>Recherche</span>
      </NavLink>
      <NavLink to="/machines">
        <Monitor />
        <span>Machines</span>
      </NavLink>
      <NavLink to="/wishlist">
        <Heart />
        <span>Wishlist</span>
      </NavLink>
    </nav>
  );
}
function Cover({ game }: { game: GameRecord }) {
  return game.coverUrl ? (
    <img className="cover" src={game.coverUrl} alt={`Jaquette de ${game.title}`} />
  ) : (
    <div className="cover placeholder">
      <Gamepad2 />
    </div>
  );
}
function PlatformLogo({
  platform,
}: {
  platform: Pick<PlatformSummary, 'name' | 'code' | 'logoUrl'>;
}) {
  const file = platformLogoFile(platform);
  const source = platform.logoUrl || (file ? `/platform-logos/${file}` : null);
  return source ? (
    <img
      className={`viewer-platform-logo${file && invertedPlatformLogos.has(file) ? ' viewer-platform-logo--inverted' : ''}`}
      src={source}
      alt={`Logo ${platform.name}`}
    />
  ) : (
    <Gamepad2 className="viewer-platform-logo-placeholder" aria-hidden="true" />
  );
}
function Splash() {
  return (
    <main className="splash">
      <img src="/logo.png" alt="Turk182! Pixel Archive" />
      <p>Ouverture de votre collection…</p>
    </main>
  );
}
function EmptyState({ text }: { text: string }) {
  return (
    <div className="empty-state">
      <Search />
      <p>{text}</p>
    </div>
  );
}
function ErrorState({ text }: { text: string }) {
  return (
    <div className="error-state">
      <strong>Impossible d’afficher les données</strong>
      <p>{text}</p>
    </div>
  );
}
